import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import Modal from '../../components/Modal';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../lib/api';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../hooks/useBloccoRecordContesto';
import { usePermesso } from '../../context/PermessiContext';
import { formatNumber } from '../../utils/formatters';
import { Societa, SocietaAmministratore, SocietaSocio, TipoSocio } from '../../types';
import { DATABASE_GO } from '../../lib/gestionaleOpen';
import { API_DOCS } from './SocietaDettaglioPage.api';

type TabKey = 'dati' | 'amministratori' | 'soci';

type DatiGeneraliForm = {
  nome: string;
  dominio: string;
  attiva: boolean;
  coloreAccento: string;
  ragioneSociale: string;
  sedeLegaleVia: string;
  sedeLegaleCivico: string;
  sedeLegaleCap: string;
  sedeLegaleCitta: string;
  sedeLegaleProvincia: string;
  sedeLegaleNazione: string;
  partitaIva: string;
  codiceFiscale: string;
  rea: string;
  codiceSdi: string;
  pec: string;
  capitaleSociale: string;
  databaseGO: string;
};

const EMPTY_DATI: DatiGeneraliForm = {
  nome: '', dominio: '', attiva: true, coloreAccento: '#5B4FB5',
  ragioneSociale: '',
  sedeLegaleVia: '', sedeLegaleCivico: '', sedeLegaleCap: '',
  sedeLegaleCitta: '', sedeLegaleProvincia: '', sedeLegaleNazione: 'IT',
  partitaIva: '', codiceFiscale: '', rea: '', codiceSdi: '', pec: '',
  capitaleSociale: '',
  databaseGO: '',
};

function fromSocieta(s: Societa): DatiGeneraliForm {
  return {
    nome: s.nome ?? '',
    dominio: s.dominio ?? '',
    attiva: !!s.attiva,
    coloreAccento: s.coloreAccento ?? '#5B4FB5',
    ragioneSociale: s.ragioneSociale ?? '',
    sedeLegaleVia: s.sedeLegaleVia ?? '',
    sedeLegaleCivico: s.sedeLegaleCivico ?? '',
    sedeLegaleCap: s.sedeLegaleCap ?? '',
    sedeLegaleCitta: s.sedeLegaleCitta ?? '',
    sedeLegaleProvincia: s.sedeLegaleProvincia ?? '',
    sedeLegaleNazione: s.sedeLegaleNazione ?? 'IT',
    partitaIva: s.partitaIva ?? '',
    codiceFiscale: s.codiceFiscale ?? '',
    rea: s.rea ?? '',
    codiceSdi: s.codiceSdi ?? '',
    pec: s.pec ?? '',
    capitaleSociale: s.capitaleSociale != null ? String(s.capitaleSociale) : '',
    databaseGO: s.databaseGO ?? '',
  };
}

// Stato scadenza documento → variante badge
function scadenzaInfo(dataIso?: string | null): { label: string; cls: string } | null {
  if (!dataIso) return null;
  const d = new Date(dataIso);
  if (isNaN(d.getTime())) return null;
  const oggi = new Date();
  const giorni = Math.floor((d.getTime() - oggi.getTime()) / 86400000);
  if (giorni < 0)  return { label: 'Scaduto',     cls: 'bop-badge bop-badge-danger' };
  if (giorni < 30) return { label: 'In scadenza', cls: 'bop-badge bop-badge-warning' };
  return { label: 'OK', cls: 'bop-badge bop-badge-success' };
}

function fmtData(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function isoToInputDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────────────────────

export default function SocietaDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermesso();
  const canEdit   = can('impostazioni.societa', 'edit');
  const canDelete = can('impostazioni.societa', 'delete');

  const societaId = id ? parseInt(id, 10) : NaN;

  const [societa, setSocieta]               = useState<Societa | null>(null);
  const [amministratori, setAmministratori] = useState<SocietaAmministratore[]>([]);
  const [soci, setSoci]                     = useState<SocietaSocio[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [savingDati, setSavingDati]         = useState(false);
  const [tab, setTab]                       = useState<TabKey>('dati');

  const [form, setForm] = useState<DatiGeneraliForm>(EMPTY_DATI);
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');

  // Hooks dirty-check + blocco contesto società
  const isDirty = initialSnapshot !== '' && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, societa ? `Scheda società ${societa.nome}` : 'Scheda società');
  useBloccoRecordContesto({
    recordSocietaId: societa?.id,
    label: societa ? `Società ${societa.nome}` : 'Società',
  });

  // ── Caricamento dati ──────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!Number.isFinite(societaId)) { setError('ID società non valido'); setLoading(false); return; }
    setLoading(true);
    try {
      // @api GET /api/societa/:id -> {"societa":{"id":"number","nome":"string","amministratori":"array","soci":"array"}}
      const r = await apiFetch<{ societa: Societa }>(`/api/societa/${societaId}`);
      const s = r.societa;
      setSocieta(s);
      setAmministratori(s.amministratori ?? []);
      setSoci(s.soci ?? []);
      const snap = fromSocieta(s);
      setForm(snap);
      setInitialSnapshot(JSON.stringify(snap));
      setLoading(false);
    } catch (e: any) {
      setError(e.message || 'Errore caricamento');
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Salvataggio dati generali ─────────────────────────────────────────────
  const salvaDatiGenerali = async () => {
    if (!form.nome.trim() || !form.dominio.trim()) { setError('Nome e dominio sono obbligatori'); return; }
    setSavingDati(true); setError('');
    try {
      const payload = {
        ...form,
        capitaleSociale: form.capitaleSociale === '' ? null : Number(form.capitaleSociale.replace(',', '.')),
      };
      // @api PUT /api/societa/:id {"nome":"string","dominio":"string","ragioneSociale":"string?","partitaIva":"string?","capitaleSociale":"number?"} -> {"societa":{...}}
      const r = await apiFetch<{ societa: Societa }>(`/api/societa/${societaId}`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      setSocieta(prev => prev ? { ...prev, ...r.societa } : r.societa);
      const snap = fromSocieta(r.societa);
      setForm(snap);
      setInitialSnapshot(JSON.stringify(snap));
    } catch (e: any) { setError(e.message || 'Errore salvataggio'); }
    finally { setSavingDati(false); }
  };

  // ── Compagine sociale: somma quote per warning ────────────────────────────
  const sommaQuote = useMemo(
    () => soci.reduce((acc, s) => acc + Number(s.quotaPercentuale || 0), 0),
    [soci]
  );

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="bop-page-header">
          <h1 className="bop-page-title">Società</h1>
        </div>
        <div className="bop-card"><div className="inline-loading">Caricamento…</div></div>
      </Layout>
    );
  }

  if (!societa) {
    return (
      <Layout>
        <div className="bop-page-header">
          <h1 className="bop-page-title">Società non trovata</h1>
        </div>
        {error && <div className="bop-inline-error">{error}</div>}
      </Layout>
    );
  }

  return (
    <Layout>
      <BopBackLink to="/impostazioni/societa" label="Torna alle società" />
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">
            <span
              className="bop-societa-color-dot"
              style={{ backgroundColor: societa.coloreAccento ?? '#5B4FB5' }}
            />
            {' '}{societa.nome}
          </h1>
          <p className="bop-text-muted">{societa.dominio}</p>
        </div>
      </div>

      <div className="bop-tabs">
        <button className={`bop-tab ${tab === 'dati' ? 'active' : ''}`} onClick={() => setTab('dati')}>
          Dati generali
        </button>
        <button className={`bop-tab ${tab === 'amministratori' ? 'active' : ''}`} onClick={() => setTab('amministratori')}>
          Amministratori ({amministratori.length})
        </button>
        <button className={`bop-tab ${tab === 'soci' ? 'active' : ''}`} onClick={() => setTab('soci')}>
          Compagine sociale ({soci.length})
        </button>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {tab === 'dati' && (
        <DatiGeneraliTab
          form={form}
          setForm={setForm}
          onSave={salvaDatiGenerali}
          saving={savingDati}
          isDirty={isDirty}
          canEdit={canEdit}
        />
      )}

      {tab === 'amministratori' && (
        <AmministratoriTab
          societaId={societaId}
          amministratori={amministratori}
          onReload={loadAll}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      {tab === 'soci' && (
        <SociTab
          societaId={societaId}
          soci={soci}
          sommaQuote={sommaQuote}
          onReload={loadAll}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Dati generali
// ─────────────────────────────────────────────────────────────────────────────

interface DatiTabProps {
  form: DatiGeneraliForm;
  setForm: React.Dispatch<React.SetStateAction<DatiGeneraliForm>>;
  onSave: () => void;
  saving: boolean;
  isDirty: boolean;
  canEdit: boolean;
}

function DatiGeneraliTab({ form, setForm, onSave, saving, isDirty, canEdit }: DatiTabProps) {
  const upd = <K extends keyof DatiGeneraliForm>(k: K, v: DatiGeneraliForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  return (
    <>
      <div className="bop-card">
        <div className="bop-card__header">
          <h2 className="bop-card__title">Identificativi</h2>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Nome visualizzato <span className="req">*</span></label>
              <input className="bop-input" value={form.nome}
                onChange={(e) => upd('nome', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Ragione sociale</label>
              <input className="bop-input" value={form.ragioneSociale}
                onChange={(e) => upd('ragioneSociale', e.target.value)} disabled={!canEdit} />
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Dominio email <span className="req">*</span></label>
              <input className="bop-input" placeholder="es. azienda.it"
                value={form.dominio} onChange={(e) => upd('dominio', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">PEC</label>
              <input className="bop-input" placeholder="es. pec@pec.azienda.it"
                value={form.pec} onChange={(e) => upd('pec', e.target.value)} disabled={!canEdit} />
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Colore società</label>
              <div className="bop-color-picker-row">
                <input
                  type="color"
                  className="bop-color-input"
                  value={form.coloreAccento}
                  onChange={(e) => upd('coloreAccento', e.target.value)}
                  disabled={!canEdit}
                />
                <span className="bop-color-hex-label">{form.coloreAccento}</span>
              </div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Stato</label>
              <label className="form-check">
                <input type="checkbox" checked={form.attiva}
                  onChange={(e) => upd('attiva', e.target.checked)} disabled={!canEdit} />
                {' '}Società attiva
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h2 className="bop-card__title">Sede legale</h2>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Indirizzo (via)</label>
              <input className="bop-input" value={form.sedeLegaleVia}
                onChange={(e) => upd('sedeLegaleVia', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Civico</label>
              <input className="bop-input" value={form.sedeLegaleCivico}
                onChange={(e) => upd('sedeLegaleCivico', e.target.value)} disabled={!canEdit} />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">CAP</label>
              <input className="bop-input" maxLength={10} value={form.sedeLegaleCap}
                onChange={(e) => upd('sedeLegaleCap', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Città</label>
              <input className="bop-input" value={form.sedeLegaleCitta}
                onChange={(e) => upd('sedeLegaleCitta', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Provincia</label>
              <input className="bop-input" maxLength={4} value={form.sedeLegaleProvincia}
                onChange={(e) => upd('sedeLegaleProvincia', e.target.value.toUpperCase())} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Nazione</label>
              <input className="bop-input" maxLength={4} value={form.sedeLegaleNazione}
                onChange={(e) => upd('sedeLegaleNazione', e.target.value.toUpperCase())} disabled={!canEdit} />
            </div>
          </div>
        </div>
      </div>

      <div className="bop-card">
        <div className="bop-card__header">
          <h2 className="bop-card__title">Dati fiscali e camerali</h2>
        </div>
        <div className="bop-card__body">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Partita IVA</label>
              <input className="bop-input" maxLength={20} value={form.partitaIva}
                onChange={(e) => upd('partitaIva', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Codice Fiscale</label>
              <input className="bop-input" maxLength={20} value={form.codiceFiscale}
                onChange={(e) => upd('codiceFiscale', e.target.value.toUpperCase())} disabled={!canEdit} />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">REA</label>
              <input className="bop-input" value={form.rea}
                onChange={(e) => upd('rea', e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Codice SDI</label>
              <input className="bop-input" maxLength={10} value={form.codiceSdi}
                onChange={(e) => upd('codiceSdi', e.target.value.toUpperCase())} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Capitale sociale (€)</label>
              <input className="bop-input" type="text" inputMode="decimal"
                placeholder="es. 100000,00"
                value={form.capitaleSociale}
                onChange={(e) => upd('capitaleSociale', e.target.value)} disabled={!canEdit} />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Database Gestionale Open</label>
              <select className="bop-select" value={form.databaseGO}
                onChange={(e) => upd('databaseGO', e.target.value)} disabled={!canEdit}>
                <option value="">— nessuno: questa società non ha dati in GO —</option>
                {DATABASE_GO.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <span className="bop-text-muted">
                Alimenta BOP - GO → Dati commerciali. In GO ogni società ha il proprio
                database: la corrispondenza va scelta a mano, non è deducibile dal nome.
              </span>
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="bop-flex bop-flex-between">
          <span className="bop-text-muted">{isDirty ? 'Modifiche non salvate' : 'Nessuna modifica'}</span>
          <button className="bop-btn bop-btn-primary" onClick={onSave} disabled={saving || !isDirty}>
            {saving ? 'Salvataggio…' : 'Salva dati generali'}
          </button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Amministratori
// ─────────────────────────────────────────────────────────────────────────────

interface AmmTabProps {
  societaId: number;
  amministratori: SocietaAmministratore[];
  onReload: () => Promise<void>;
  canEdit: boolean;
  canDelete: boolean;
}

function AmministratoriTab({ societaId, amministratori, onReload, canEdit, canDelete }: AmmTabProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<SocietaAmministratore | null>(null);

  const apri = (a: SocietaAmministratore | null) => { setEditing(a); setModalOpen(true); };
  const chiudi = () => { setModalOpen(false); setEditing(null); };

  const elimina = async (a: SocietaAmministratore) => {
    if (!window.confirm(`Eliminare l'amministratore ${a.nome} ${a.cognome}?`)) return;
    // @api DELETE /api/societa/:id/amministratori/:ammId -> {"ok":true}
    await apiFetch(`/api/societa/${societaId}/amministratori/${a.id}`, { method: 'DELETE' });
    await onReload();
  };

  const scaricaDoc = async (a: SocietaAmministratore) => {
    if (!a.documentoPathGcs) return;
    // @api GET /api/societa/:id/amministratori/:ammId/documento -> binary
    await apiDownload(
      `/api/societa/${societaId}/amministratori/${a.id}/documento`,
      a.documentoNomeFile || `documento_${a.id}.bin`,
    );
  };

  return (
    <>
      <div className="bop-card">
        <div className="bop-card__header bop-flex bop-flex-between">
          <h2 className="bop-card__title">Amministratori</h2>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={() => apri(null)}>+ Nuovo amministratore</button>
          )}
        </div>
        <div className="bop-table-wrapper">
          {amministratori.length === 0 ? (
            <div className="bop-empty-state">Nessun amministratore registrato.</div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Nominativo</th>
                  <th>Carica</th>
                  <th>Codice Fiscale</th>
                  <th>Documento</th>
                  <th>Scadenza</th>
                  <th>Stato</th>
                  <th className="bop-text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {amministratori.map((a) => {
                  const sc = scadenzaInfo(a.documentoScadenza);
                  return (
                    <tr key={a.id} onClick={() => apri(a)}>
                      <td><strong>{a.nome} {a.cognome}</strong></td>
                      <td>{a.carica || '—'}</td>
                      <td className="bop-text-muted">{a.codiceFiscale || '—'}</td>
                      <td>
                        {a.documentoPathGcs ? (
                          <button
                            className="bop-btn-link"
                            onClick={(e) => { e.stopPropagation(); scaricaDoc(a); }}
                            title="Scarica documento"
                          >
                            {a.documentoNomeFile || 'Documento'}
                          </button>
                        ) : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        {a.documentoScadenza
                          ? <span className="bop-flex">{fmtData(a.documentoScadenza)}{sc && <>{' '}<span className={sc.cls}>{sc.label}</span></>}</span>
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        <span className={`bop-badge ${a.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                          {a.attivo ? 'Attivo' : 'Cessato'}
                        </span>
                      </td>
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Trash />, label: 'Elimina', variant: 'danger',
                            onClick: () => elimina(a), hidden: !canDelete },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <ModaleAmministratore
          societaId={societaId}
          iniziale={editing}
          onClose={chiudi}
          onSaved={async () => { chiudi(); await onReload(); }}
          canEdit={canEdit}
        />
      )}
    </>
  );
}

// ─── Modale Amministratore ──────────────────────────────────────────────────

type AmmForm = {
  nome: string;
  cognome: string;
  codiceFiscale: string;
  dataNascita: string;
  luogoNascita: string;
  carica: string;
  dataInizioCarica: string;
  dataFineCarica: string;
  documentoTipo: string;
  documentoNumero: string;
  documentoScadenza: string;
  attivo: boolean;
};

const EMPTY_AMM: AmmForm = {
  nome: '', cognome: '', codiceFiscale: '', dataNascita: '', luogoNascita: '',
  carica: '', dataInizioCarica: '', dataFineCarica: '',
  documentoTipo: '', documentoNumero: '', documentoScadenza: '', attivo: true,
};

function ammToForm(a: SocietaAmministratore): AmmForm {
  return {
    nome: a.nome ?? '',
    cognome: a.cognome ?? '',
    codiceFiscale: a.codiceFiscale ?? '',
    dataNascita: isoToInputDate(a.dataNascita),
    luogoNascita: a.luogoNascita ?? '',
    carica: a.carica ?? '',
    dataInizioCarica: isoToInputDate(a.dataInizioCarica),
    dataFineCarica:   isoToInputDate(a.dataFineCarica),
    documentoTipo: a.documentoTipo ?? '',
    documentoNumero: a.documentoNumero ?? '',
    documentoScadenza: isoToInputDate(a.documentoScadenza),
    attivo: !!a.attivo,
  };
}

interface ModaleAmmProps {
  societaId: number;
  iniziale: SocietaAmministratore | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  canEdit: boolean;
}

function ModaleAmministratore({ societaId, iniziale, onClose, onSaved, canEdit }: ModaleAmmProps) {
  const [form, setForm]     = useState<AmmForm>(iniziale ? ammToForm(iniziale) : EMPTY_AMM);
  const [file, setFile]     = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [doc, setDoc]       = useState<SocietaAmministratore | null>(iniziale);

  const upd = <K extends keyof AmmForm>(k: K, v: AmmForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const salva = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) { setErr('Nome e cognome obbligatori'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        codiceFiscale: form.codiceFiscale.trim() || null,
        dataNascita: form.dataNascita || null,
        luogoNascita: form.luogoNascita.trim() || null,
        carica: form.carica.trim() || null,
        dataInizioCarica: form.dataInizioCarica || null,
        dataFineCarica: form.dataFineCarica || null,
        documentoTipo: form.documentoTipo.trim() || null,
        documentoNumero: form.documentoNumero.trim() || null,
        documentoScadenza: form.documentoScadenza || null,
        attivo: form.attivo,
      };
      let ammRecord: SocietaAmministratore | null = doc;
      if (doc) {
        // @api PUT /api/societa/:id/amministratori/:ammId {"nome":"string"} -> {"amministratore":"object"}
        const r = await apiFetch<{ amministratore: SocietaAmministratore }>(
          `/api/societa/${societaId}/amministratori/${doc.id}`,
          { method: 'PUT', body: JSON.stringify(payload) },
        );
        ammRecord = r.amministratore;
      } else {
        // @api POST /api/societa/:id/amministratori {"nome":"string","cognome":"string"} -> {"amministratore":"object"}
        const r = await apiFetch<{ amministratore: SocietaAmministratore }>(
          `/api/societa/${societaId}/amministratori`,
          { method: 'POST', body: JSON.stringify(payload) },
        );
        ammRecord = r.amministratore;
      }

      // Upload documento se selezionato
      if (file && ammRecord) {
        const fd = new FormData();
        fd.append('file', file);
        if (form.documentoTipo)     fd.append('documentoTipo', form.documentoTipo);
        if (form.documentoNumero)   fd.append('documentoNumero', form.documentoNumero);
        if (form.documentoScadenza) fd.append('documentoScadenza', form.documentoScadenza);
        // @api POST /api/societa/:id/amministratori/:ammId/documento multipart -> {"amministratore":"object"}
        await apiFetch(
          `/api/societa/${societaId}/amministratori/${ammRecord.id}/documento`,
          { method: 'POST', body: fd },
        );
      }
      await onSaved();
    } catch (e: any) {
      setErr(e.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const eliminaDocumento = async () => {
    if (!doc || !doc.documentoPathGcs) return;
    if (!window.confirm('Eliminare il documento caricato?')) return;
    // @api DELETE /api/societa/:id/amministratori/:ammId/documento -> {"amministratore":"object"}
    const r = await apiFetch<{ amministratore: SocietaAmministratore }>(
      `/api/societa/${societaId}/amministratori/${doc.id}/documento`,
      { method: 'DELETE' },
    );
    setDoc(r.amministratore);
  };

  return (
    <Modal
      title={doc ? `Modifica amministratore` : 'Nuovo amministratore'}
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </button>
          )}
        </>
      }
    >
      {err && <div className="bop-inline-error">{err}</div>}

      <h3 className="bop-section-title">Anagrafica</h3>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Nome <span className="req">*</span></label>
          <input className="bop-input" value={form.nome} onChange={(e) => upd('nome', e.target.value)} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Cognome <span className="req">*</span></label>
          <input className="bop-input" value={form.cognome} onChange={(e) => upd('cognome', e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Codice Fiscale</label>
          <input className="bop-input" maxLength={20} value={form.codiceFiscale}
            onChange={(e) => upd('codiceFiscale', e.target.value.toUpperCase())} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Data di nascita</label>
          <input className="bop-input" type="date" value={form.dataNascita}
            onChange={(e) => upd('dataNascita', e.target.value)} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Luogo di nascita</label>
          <input className="bop-input" value={form.luogoNascita}
            onChange={(e) => upd('luogoNascita', e.target.value)} disabled={!canEdit} />
        </div>
      </div>

      <h3 className="bop-section-title">Carica</h3>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Carica</label>
          <input className="bop-input" placeholder="es. Presidente, Amministratore Delegato, Consigliere…"
            value={form.carica} onChange={(e) => upd('carica', e.target.value)} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Inizio carica</label>
          <input className="bop-input" type="date" value={form.dataInizioCarica}
            onChange={(e) => upd('dataInizioCarica', e.target.value)} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Fine carica</label>
          <input className="bop-input" type="date" value={form.dataFineCarica}
            onChange={(e) => upd('dataFineCarica', e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <div className="bop-form-group">
        <label className="form-check">
          <input type="checkbox" checked={form.attivo}
            onChange={(e) => upd('attivo', e.target.checked)} disabled={!canEdit} />
          {' '}In carica
        </label>
      </div>

      <h3 className="bop-section-title">Documento di identità</h3>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Tipo documento</label>
          <select className="bop-select" value={form.documentoTipo}
            onChange={(e) => upd('documentoTipo', e.target.value)} disabled={!canEdit}>
            <option value="">—</option>
            <option value="CARTA_IDENTITA">Carta d'identità</option>
            <option value="PASSAPORTO">Passaporto</option>
            <option value="PATENTE">Patente</option>
            <option value="ALTRO">Altro</option>
          </select>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Numero documento</label>
          <input className="bop-input" value={form.documentoNumero}
            onChange={(e) => upd('documentoNumero', e.target.value)} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Scadenza</label>
          <input className="bop-input" type="date" value={form.documentoScadenza}
            onChange={(e) => upd('documentoScadenza', e.target.value)} disabled={!canEdit} />
        </div>
      </div>

      <div className="bop-form-group">
        <label className="bop-label">File documento (PDF/JPG/PNG)</label>
        {doc?.documentoPathGcs ? (
          <div className="bop-flex bop-flex-between">
            <span>📎 {doc.documentoNomeFile}</span>
            <div className="bop-flex">
              <button className="bop-btn bop-btn-secondary" type="button"
                onClick={() => apiDownload(
                  `/api/societa/${societaId}/amministratori/${doc.id}/documento`,
                  doc.documentoNomeFile || 'documento.bin',
                )}>
                Scarica
              </button>
              {canEdit && (
                <button className="bop-btn bop-btn-danger" type="button" onClick={eliminaDocumento}>
                  Rimuovi
                </button>
              )}
            </div>
          </div>
        ) : null}
        {canEdit && (
          <input
            type="file"
            className="bop-input"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        )}
        {file && <p className="bop-text-muted">Nuovo file selezionato: {file.name}</p>}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Compagine sociale (Soci)
// ─────────────────────────────────────────────────────────────────────────────

interface SociTabProps {
  societaId: number;
  soci: SocietaSocio[];
  sommaQuote: number;
  onReload: () => Promise<void>;
  canEdit: boolean;
  canDelete: boolean;
}

function SociTab({ societaId, soci, sommaQuote, onReload, canEdit, canDelete }: SociTabProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<SocietaSocio | null>(null);

  const apri = (s: SocietaSocio | null) => { setEditing(s); setModalOpen(true); };
  const chiudi = () => { setModalOpen(false); setEditing(null); };

  const elimina = async (s: SocietaSocio) => {
    if (!window.confirm(`Eliminare il socio ${s.denominazione}?`)) return;
    // @api DELETE /api/societa/:id/soci/:socioId -> {"ok":true}
    await apiFetch(`/api/societa/${societaId}/soci/${s.id}`, { method: 'DELETE' });
    await onReload();
  };

  const quoteWarning =
    sommaQuote > 100.0001 ? 'Somma quote oltre il 100% — verificare i dati.' :
    (soci.length > 0 && sommaQuote < 99.9999) ? `Somma quote: ${formatNumber(sommaQuote)}% — risulta inferiore al 100%.` :
    null;

  return (
    <>
      <div className="bop-card">
        <div className="bop-card__header bop-flex bop-flex-between">
          <h2 className="bop-card__title">Compagine sociale</h2>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={() => apri(null)}>+ Nuovo socio</button>
          )}
        </div>

        {quoteWarning && (
          <div className="bop-inline-warning">{quoteWarning}</div>
        )}

        <div className="bop-table-wrapper">
          {soci.length === 0 ? (
            <div className="bop-empty-state">Nessun socio registrato.</div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Denominazione</th>
                  <th>Tipo</th>
                  <th>CF / P.IVA</th>
                  <th className="bop-text-right">Quota %</th>
                  <th>Stato</th>
                  <th className="bop-text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {soci.map((s) => (
                  <tr key={s.id} onClick={() => apri(s)}>
                    <td><strong>{s.denominazione}</strong></td>
                    <td>
                      <span className="bop-badge bop-badge-info">
                        {s.tipo === 'PG' ? 'Persona giuridica' : 'Persona fisica'}
                      </span>
                    </td>
                    <td className="bop-text-muted">{s.codiceFiscalePIva || '—'}</td>
                    <td className="bop-text-right">{formatNumber(Number(s.quotaPercentuale))}%</td>
                    <td>
                      <span className={`bop-badge ${s.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                        {s.attivo ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="bop-text-right">
                      <BopRowActions actions={[
                        { icon: <RowIcon.Trash />, label: 'Elimina', variant: 'danger',
                          onClick: () => elimina(s), hidden: !canDelete },
                      ]} />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3}><strong>Totale quote</strong></td>
                  <td className="bop-text-right"><strong>{formatNumber(sommaQuote)}%</strong></td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && (
        <ModaleSocio
          societaId={societaId}
          iniziale={editing}
          onClose={chiudi}
          onSaved={async () => { chiudi(); await onReload(); }}
          canEdit={canEdit}
        />
      )}
    </>
  );
}

// ─── Modale Socio ───────────────────────────────────────────────────────────

type SocioForm = {
  denominazione: string;
  tipo: TipoSocio;
  codiceFiscalePIva: string;
  quotaPercentuale: string;
  attivo: boolean;
};

const EMPTY_SOCIO: SocioForm = {
  denominazione: '', tipo: 'PF', codiceFiscalePIva: '', quotaPercentuale: '0', attivo: true,
};

function socioToForm(s: SocietaSocio): SocioForm {
  return {
    denominazione: s.denominazione ?? '',
    tipo: s.tipo,
    codiceFiscalePIva: s.codiceFiscalePIva ?? '',
    quotaPercentuale: s.quotaPercentuale != null ? String(s.quotaPercentuale) : '0',
    attivo: !!s.attivo,
  };
}

interface ModaleSocioProps {
  societaId: number;
  iniziale: SocietaSocio | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  canEdit: boolean;
}

function ModaleSocio({ societaId, iniziale, onClose, onSaved, canEdit }: ModaleSocioProps) {
  const [form, setForm]     = useState<SocioForm>(iniziale ? socioToForm(iniziale) : EMPTY_SOCIO);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const upd = <K extends keyof SocioForm>(k: K, v: SocioForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const salva = async () => {
    if (!form.denominazione.trim()) { setErr('Denominazione obbligatoria'); return; }
    const q = Number(form.quotaPercentuale.replace(',', '.'));
    if (isNaN(q) || q < 0 || q > 100) { setErr('Quota deve essere tra 0 e 100'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        denominazione: form.denominazione.trim(),
        tipo: form.tipo,
        codiceFiscalePIva: form.codiceFiscalePIva.trim() || null,
        quotaPercentuale: q,
        attivo: form.attivo,
      };
      if (iniziale) {
        // @api PUT /api/societa/:id/soci/:socioId -> {"socio":"object"}
        await apiFetch(`/api/societa/${societaId}/soci/${iniziale.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/societa/:id/soci -> {"socio":"object"}
        await apiFetch(`/api/societa/${societaId}/soci`, {
          method: 'POST', body: JSON.stringify(payload),
        });
      }
      await onSaved();
    } catch (e: any) {
      setErr(e.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={iniziale ? 'Modifica socio' : 'Nuovo socio'}
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </button>
          )}
        </>
      }
    >
      {err && <div className="bop-inline-error">{err}</div>}
      <div className="bop-form-group">
        <label className="bop-label">Denominazione / Nome e cognome <span className="req">*</span></label>
        <input className="bop-input" value={form.denominazione}
          onChange={(e) => upd('denominazione', e.target.value)} disabled={!canEdit} />
      </div>
      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Tipo socio</label>
          <select className="bop-select" value={form.tipo}
            onChange={(e) => upd('tipo', (e.target.value as TipoSocio))} disabled={!canEdit}>
            <option value="PF">Persona fisica</option>
            <option value="PG">Persona giuridica</option>
          </select>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">{form.tipo === 'PG' ? 'Partita IVA' : 'Codice Fiscale'}</label>
          <input className="bop-input" value={form.codiceFiscalePIva}
            onChange={(e) => upd('codiceFiscalePIva', e.target.value.toUpperCase())} disabled={!canEdit} />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Quota %</label>
          <input className="bop-input" type="text" inputMode="decimal"
            placeholder="es. 25,50"
            value={form.quotaPercentuale}
            onChange={(e) => upd('quotaPercentuale', e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <div className="bop-form-group">
        <label className="form-check">
          <input type="checkbox" checked={form.attivo}
            onChange={(e) => upd('attivo', e.target.checked)} disabled={!canEdit} />
          {' '}Attivo
        </label>
      </div>
    </Modal>
  );
}
